/* eslint-disable no-unused-vars */
/* eslint-disable indent */
/*
 * NOTE: This component currently implements CLIENT-SIDE sorting because the backend API
 * (ReportsService.getMCNListReport) doesn't support server-side sorting parameters.
 * 
 * For better performance with large datasets, the backend should be updated to accept
 * sortBy and sortOrder parameters in the API endpoint.
 */
import React, { useState, useEffect } from "react";
import { Card, Row, Col, message, Button, Tooltip, Select, DatePicker, Table, Spin, Input } from "antd";
import moment from "moment";
import "./style.scss";
import { ReportsService } from "../../../services/api/reports";
import { showErrorMessage } from "../../../utils";
import Smcs_Loader from "../../../assests/images/loader-new.gif";
import search_icon from "../../../assests/images/search-icon.svg";

const { RangePicker } = DatePicker;

const McnShipmentList = () => {
    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState([]);

    const [dateRange, setDateRange] = useState([
        moment().subtract(7, "days"),
        moment()
    ]);

    const [searchText, setSearchText] = useState("");

    const [partnerCodeList] = useState([
        { value: "SHIPYAARI", label: "Shipyaari" },
        { value: "SMILE", label: "Smile" }
    ]);
    const [selectedPartnerCodes, setSelectedPartnerCodes] = useState(null);

    const [bookingTypeList] = useState([
        { value: "COURIER", label: "Courier" },
        { value: "ECOMM", label: "Ecomm" },
        { value: "CARGO", label: "Cargo" },
        { value: "International", label: "International" }
    ]);
    const [selectedBookingTypes, setSelectedBookingTypes] = useState(null);

    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
    });

    const [sorter, setSorter] = useState({
        field: null,
        order: null
    });
    const userId = JSON.parse(localStorage.getItem("userId"));

    // Fetch report data when filters or sorting change (pagination is now handled client-side)
    useEffect(() => {
        // Only fetch if we have valid dates
        const hasValidDates = Array.isArray(dateRange) && dateRange[0] && dateRange[1] && typeof dateRange[0].format === "function" && typeof dateRange[1].format === "function";

        if (hasValidDates) {
            // Debounce the API call to prevent rapid successive calls
            const timeoutId = setTimeout(() => {
                fetchReport();
            }, 100);

            return () => clearTimeout(timeoutId);
        }
        // eslint-disable-next-line
    }, [selectedPartnerCodes, selectedBookingTypes, dateRange, sorter, searchText]);

    const fetchReport = async () => {
        setLoading(true);
        try {
            setReportData([]);
            // Guard against invalid dateRange
            const hasValidDates = Array.isArray(dateRange) && dateRange[0] && dateRange[1] && typeof dateRange[0].format === "function" && typeof dateRange[1].format === "function";
            if (!hasValidDates) {
                setLoading(false);
                return;
            }
            const params = {
                ...(hasValidDates && {
                    fromDate: dateRange[0].format("DD-MM-YYYY"),
                    toDate: dateRange[1].format("DD-MM-YYYY")
                }),
                ...(selectedPartnerCodes && { partnerCodes: selectedPartnerCodes }),
                ...(selectedBookingTypes && { bookingTypes: selectedBookingTypes }),
                ...(searchText && { search: searchText }),
                ...(pagination && { page: pagination.current, size: pagination.pageSize })
                // Note: Removed page/size params to get all data for client-side sorting and pagination
            };

            console.log("MCN Shipment List Report params:", params);
            console.log("Current sorter state:", sorter);
            console.log("Current pagination state:", pagination);

            if (sorter.field && sorter.order) {
                console.log(`Sorting by ${sorter.field} in ${sorter.order} order`);
            } else {
                console.log("No sorting applied");
            }

            // Get active hub or user premise id
            const activeHub = JSON.parse(localStorage.getItem("active_hub"));
            const currentPremiseId = activeHub?.hubId || userId?.userInfo?.premiseId;

            // Call the MCN List report API
            const { data, total } = await ReportsService.getMCNListReport(params, currentPremiseId);

            console.log("API Response data:", data);
            console.log("API Response total:", total);

            if (!data || data.length === 0) {
                message.info("No MCN shipment data found for the selected criteria");
                setReportData([]);
                setPagination(prev => ({
                    ...prev,
                    total: 0,
                }));
                return;
            }

            // Apply client-side sorting since backend doesn't support it yet
            let sortedData = [...(data || [])];
            if (sorter.field && sorter.order) {
                sortedData.sort((a, b) => {
                    const aValue = a[sorter.field];
                    const bValue = b[sorter.field];

                    // Handle null/undefined values
                    if (aValue == null && bValue == null) return 0;
                    if (aValue == null) return sorter.order === "ascend" ? -1 : 1;
                    if (bValue == null) return sorter.order === "ascend" ? 1 : -1;

                    let comparison = 0;

                    // Special handling for date fields
                    if (sorter.field.includes("Date") || sorter.field.includes("Time")) {
                        const aDate = new Date(aValue);
                        const bDate = new Date(bValue);
                        comparison = aDate.getTime() - bDate.getTime();
                    } else {
                        // String comparison for other fields
                        const aStr = String(aValue).toLowerCase();
                        const bStr = String(bValue).toLowerCase();

                        if (aStr < bStr) comparison = -1;
                        if (aStr > bStr) comparison = 1;
                    }

                    return sorter.order === "ascend" ? comparison : -comparison;
                });
                console.log(`Applied client-side sorting: ${sorter.field} ${sorter.order}`);
            } else {
                console.log("No sorting applied - displaying data in original order");
            }

            // Apply client-side pagination after sorting
            const totalRecords = sortedData.length;
            const startIndex = (pagination.current) * pagination.pageSize;
            const endIndex = startIndex + pagination.pageSize;
            const paginatedData = sortedData.slice(startIndex, endIndex);

            console.log(`Total records: ${totalRecords}, Showing: ${startIndex + 1}-${Math.min(endIndex, totalRecords)}`);

            setReportData(sortedData);
            setPagination(prev => ({
                ...prev,
                total: total,
            }));
        } catch (error) {
            console.error("Detailed API Error:", error);
            console.error("Error message:", error.message);
            console.error("Error response:", error.response);
            showErrorMessage(`Failed to fetch report data: ${error.message || "Unknown error"}`);
            setReportData([]);
            setPagination(prev => ({
                ...prev,
                total: 0
            }));
        } finally {
            setLoading(false);
        }
    };

    const handleTableChange = (newPagination, filters, newSorter) => {
        console.log("Table change:", { newPagination, filters, newSorter }); // Debug log
        console.log("Previous sorter state:", sorter); // Debug log

        // Check if sorting has changed
        const sortingChanged = (
            (newSorter && newSorter.field !== sorter.field) ||
            (newSorter && newSorter.order !== sorter.order) ||
            (!newSorter && sorter.field) || // Clearing sort completely
            (!newSorter?.order && sorter.order) // Clearing sort on same column
        );

        // If sorting changed, reset to page 1, otherwise use the new pagination
        const targetPage = sortingChanged ? 1 : newPagination.current;

        // Update pagination and sorting together
        setPagination(prev => ({
            ...prev,
            current: newPagination.current,
            pageSize: newPagination.pageSize
        }));

        // Handle sorting changes - ensure we have valid sorter data
        if (newSorter && (newSorter.field || newSorter.column) && newSorter.order) {
            const newSorterState = {
                field: newSorter.field || newSorter.column?.key,
                order: newSorter.order
            };
            console.log("Setting new sorter:", newSorterState); // Debug log
            setSorter(newSorterState);
        } else {
            // Clear sorting if no sorter or no order
            console.log("Clearing sorter"); // Debug log
            setSorter({
                field: null,
                order: null
            });
        }
        fetchReport();
    };

    // Handler for AWB number click to show shipment details
    const handleAwbClick = async (awbNumber, record) => {
        try {
            const shipmentData = {
                awbNumber: awbNumber,
                ...record
            };

            // Store data in session storage for shipment tracker
            sessionStorage.setItem("SHIPMENT_TRACKER_DATA", JSON.stringify(shipmentData));

            // Open shipment tracker in new tab
            window.open(`${window.location.origin}/shipment-tracker?awb=${awbNumber}`, "_blank");

        } catch (error) {
            console.error("Error handling AWB click:", error);
            message.error("Failed to open shipment details");
        }
    };

    const columns = [
        {
            title: "AWB Number",
            dataIndex: "awbNumber",
            key: "awbNumber",
            sorter: true,
            render: (text, record) => (
                <Tooltip title="Click to view shipment details">
                    <span
                        style={{
                            color: "#2977ff",
                            cursor: "pointer",
                            textDecoration: "underline",
                            fontWeight: "500"
                        }}
                        onClick={() => handleAwbClick(text, record)}
                    >
                        {text}
                    </span>
                </Tooltip>
            )
        },
        {
            title: "3PL AWB",
            dataIndex: "tplAwbNumber",
            key: "tplAwbNumber",
            sorter: true, render: (text) => {
                return text;
            }
        },
        {
            title: "Booking Complete Time",
            dataIndex: "bookingCompleteTime",
            key: "bookingCompleteTime",
            sorter: true,
            render: (text) => {
                if (!text) return "-";
                return text;
            }
        },
        {
            title: "Inscan Date",
            dataIndex: "inscanDate",
            key: "inscanDate",
            sorter: true,
            render: (text) => {
                if (!text) return <span style={{ color: "#999" }}>Not Scanned</span>;
                return text;
            }
        },
        {
            title: "Partner Code",
            dataIndex: "partnerCode",
            key: "partnerCode",
            sorter: true,
            align: "center",
            render: (text) => {
                const displayText = text || "NA";
                const getPartnerStyle = (value) => {
                    if (!value || value === "NA") {
                        return { bg: "#f5f5f5", color: "#999" };
                    }
                    return { bg: "#f5f5f5", color: "#666" };
                };
                const style = getPartnerStyle(text);
                return (
                    <span style={{
                        background: style.bg,
                        color: style.color,
                        padding: "4px 8px",
                        borderRadius: "4px",
                        fontSize: "12px",
                        fontWeight: "bold"
                    }}>
                        {displayText}
                    </span>
                );
            }
        },
        {
            title: "Booking Type",
            dataIndex: "bookingType",
            key: "bookingType",
            sorter: true,
            align: "center",
            render: (text) => {
                const displayText = text || "NA";
                const getBookingTypeStyle = (value) => {
                    if (!value || value === "NA") {
                        return { bg: "#f5f5f5", color: "#999" };
                    }
                    return { bg: "#f5f5f5", color: "#666" };
                };
                const style = getBookingTypeStyle(text);
                return (
                    <span style={{
                        background: style.bg,
                        color: style.color,
                        padding: "4px 8px",
                        borderRadius: "4px",
                        fontSize: "12px",
                        fontWeight: "bold"
                    }}>
                        {displayText}
                    </span>
                );
            }
        },
        {
            title: "Booking Status",
            dataIndex: "bookingStatus",
            key: "bookingStatus",
            sorter: true,
            align: "center",
            render: (text) => {
                const displayText = text ? text.replace(/_/g, " ") : "NA";
                const getStatusColor = (status) => {
                    if (!status || status === "NA") {
                        return { bg: "#f5f5f5", color: "#999" };
                    }
                    return { bg: "#f5f5f5", color: "#666" };
                };
                const style = getStatusColor(text);
                return (
                    <span style={{
                        background: style.bg,
                        color: style.color,
                        padding: "4px 8px",
                        borderRadius: "4px",
                        fontSize: "11px",
                        fontWeight: "500"
                    }}>
                        {displayText}
                    </span>
                );
            }
        }
    ];

    return (
        <div className="consolidated-manifest-cntr">
            <Card className="report-card">
                <Row gutter={[16, 16]} className="filters-section">
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Booking Date</div>
                        <RangePicker
                            className="custom-calendar"
                            value={dateRange && dateRange[0] && dateRange[1] ? dateRange : [moment(), moment()]}
                            onChange={setDateRange}
                            format="YYYY-MM-DD"
                            allowClear
                            style={{ width: "100%", height: "40px" }}
                        />
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Partner Code Type</div>
                        <Select
                            className="filter-select"
                            style={{
                                width: "100%"
                            }}
                            placeholder="Select Partner Code"
                            value={selectedPartnerCodes}
                            onChange={setSelectedPartnerCodes}
                            allowClear
                            showSearch
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label || "").toLowerCase().includes(input.toLowerCase())
                            }
                            options={partnerCodeList}
                        />
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Booking Type</div>
                        <Select
                            className="filter-select"
                            style={{
                                width: "100%"
                            }}
                            placeholder="Select Booking Type"
                            value={selectedBookingTypes}
                            onChange={setSelectedBookingTypes}
                            allowClear
                            showSearch
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label || "").toLowerCase().includes(input.toLowerCase())
                            }
                            options={bookingTypeList}
                        />
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Search</div>
                        <Input
                            className="mr-1"
                            placeholder="Enter AWB number to search"
                            // disabled={!dateRangeStatus || tableData?.length == 0}
                            onChange={(e) => setSearchText(e.target.value)}
                            onSearch={(value) => setSearchText(value)}
                            bordered
                            style={{
                                borderRadius: "5px",
                                height: "40px",
                            }}
                            maxLength={20}
                            suffix={<img src={search_icon} />}
                        />
                    </Col>
                </Row>
            </Card>

            <div style={{ marginTop: 16 }}>
                <Spin
                    spinning={loading}
                    indicator={
                        <img src={Smcs_Loader} style={{ height: "100px", width: "100px" }} />
                    }
                >
                    <Table
                        className="table-head-sticky"
                        columns={columns.map(col => ({
                            ...col,
                            sortOrder: sorter.field === col.key ? sorter.order : null,
                            sorter: col.sorter // Ensure sorter is explicitly set
                        }))}
                        dataSource={reportData}
                        onChange={handleTableChange}
                        pagination={pagination}
                        rowKey="lcrNum"
                        scroll={{ x: 800 }}
                        showSorterTooltip={true}
                    />
                </Spin>
            </div>
        </div>
    );
};

export default McnShipmentList;
